import { useState } from 'react';
import { ACCENT, ACCENT_SOFT, CONTEXT, SURFACE, SURFACE_MUTED } from './palette';

/**
 * Who owns each layer — the assembled stack against the Fnk stack.
 * One CSS grid carries both columns so the rows align. The argument is the seams.
 */

interface Layer {
  name: string;
  ours: string;
  theirs: string;
  gain: string;
}

const layers: Layer[] = [
  { name: 'Harness', ours: 'Workflows, tools, policies, gates and evaluation sets', theirs: 'Built by the customer, or by an integrator, per project', gain: 'The configured layer ships with the stack and is provider-neutral — the same pack runs on Fnk’s engine or an approved endpoint.' },
  { name: 'Agent framework + control', ours: 'Orchestration, retrieval, admission, transaction budgets', theirs: 'An open-source framework, on someone else’s release cycle', gain: 'Budgets span the whole business transaction, not one request — agent fan-out stays bounded.' },
  { name: 'Inference engine', ours: 'Capacity contract, cancellation, admission-aware scheduling', theirs: 'vLLM or llama.cpp behind an HTTP boundary', gain: 'Capacity flows up and cancellation flows down. That is where the 2.0× comes from — and what a reactive framework alone cannot get.' },
];

export function VerticalIntegrationChart() {
  const [active, setActive] = useState<string | null>(null);

  return (
    <figure className="m-0">
      <div className="mb-3 grid grid-cols-2 gap-3 sm:gap-6">
        <div className="text-center">
          <span className="text-xs font-semibold text-ink-3 sm:text-sm">Assembled</span>
          <p className="mt-0.5 font-mono text-[10px] text-ink-4 sm:text-[11px]">three suppliers, two boundaries</p>
        </div>
        <div className="text-center">
          <span className="text-xs font-semibold text-ink sm:text-sm">Fnk</span>
          <p className="mt-0.5 font-mono text-[10px] text-accent-ink sm:text-[11px]">one system, one contract</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-3 sm:gap-x-6">
        {layers.map((layer, i) => {
          const dim = active !== null && active !== layer.name;
          const first = i === 0;
          const last = i === layers.length - 1;
          return (
            <div key={layer.name} className="contents">
              <div className={i > 0 ? 'pt-5 sm:pt-6' : undefined}>
                <div
                  className="h-full rounded-sm px-2 py-3 text-center transition-opacity sm:px-4 sm:py-4"
                  style={{ background: SURFACE_MUTED, border: `1px solid ${CONTEXT}55`, opacity: dim ? 0.45 : 1 }}
                >
                  <div className="text-[11px] font-medium text-ink-2 sm:text-sm">{layer.name}</div>
                  <div className="mt-1 text-[9px] leading-snug text-ink-3 sm:text-[11px]">{layer.theirs}</div>
                </div>
              </div>
              <div
                className="cursor-default px-2 py-3 text-center transition-colors sm:px-4 sm:py-4"
                style={{
                  background: active === layer.name ? ACCENT_SOFT : SURFACE,
                  borderLeft: `1.5px solid ${ACCENT}`,
                  borderRight: `1.5px solid ${ACCENT}`,
                  borderTop: first ? `1.5px solid ${ACCENT}` : `1px solid ${ACCENT}44`,
                  borderBottom: last ? `1.5px solid ${ACCENT}` : undefined,
                }}
                onMouseEnter={() => setActive(layer.name)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(layer.name)}
                onBlur={() => setActive(null)}
                tabIndex={0}
              >
                <div className="text-[11px] font-semibold text-ink sm:text-sm">{layer.name}</div>
                <div className="mt-1 text-[9px] leading-snug text-ink-2 sm:text-[11px]">{layer.ours}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-3 sm:gap-6">
        <p className="text-center font-mono text-[10px] text-ink-4 sm:text-[11px]">two vendor seams</p>
        <p className="text-center font-mono text-[10px] text-ink-3 sm:text-[11px]">no seam — a contract instead</p>
      </div>

      <div className="mt-5 min-h-[3.5rem] border-t border-line pt-4">
        {active ? (
          <p className="text-[13px] leading-relaxed text-ink-2">
            <span className="font-medium text-ink">{active}:</span> {layers.find((l) => l.name === active)?.gain}
          </p>
        ) : (
          <p className="text-[13px] leading-relaxed text-ink-2">
            <span className="font-semibold text-ink">Every seam is a place where money leaks, accountability breaks, or a decision is made by someone else.</span>{' '}
            Owning both sides of the inference boundary is why one per-GPU licence covers the whole stack. Hover a layer for what it earns.
          </p>
        )}
      </div>
    </figure>
  );
}
